import React, { useState, useEffect } from 'react';
import api from '../services/api';
import CreateCustomerModal from '../components/CreateCustomerModal';
import '../styles/CreateJob.css';

interface Customer {
  _id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
}

interface LocationInput {
  address: string;
  latitude: string;
  longitude: string;
  mapLink?: string;
}

// Utility function to parse Google Maps links and extract coordinates
const parseGoogleMapsLink = (link: string): { lat: string; lng: string } | null => {
  try {
    // Remove whitespace
    link = link.trim();

    // Pattern 1: ?q=lat,lng or &q=lat,lng
    let match = link.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) {
      return { lat: match[1], lng: match[2] };
    }

    // Pattern 2: @lat,lng (e.g., /@12.345,67.890,15z)
    match = link.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) {
      return { lat: match[1], lng: match[2] };
    }

    // Pattern 3: place/.../@lat,lng
    match = link.match(/place\/[^/]+\/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) {
      return { lat: match[1], lng: match[2] };
    }

    // Pattern 4: /maps?ll=lat,lng
    match = link.match(/[?&]ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) {
      return { lat: match[1], lng: match[2] };
    }

    return null;
  } catch (error) {
    console.error('Error parsing Google Maps link:', error);
    return null;
  }
};

const CreateJob: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);

  // Form state
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [pickupLocation, setPickupLocation] = useState<LocationInput>({
    address: '',
    latitude: '',
    longitude: '',
    mapLink: '',
  });
  const [dropoffLocation, setDropoffLocation] = useState<LocationInput>({
    address: '',
    latitude: '',
    longitude: '',
    mapLink: '',
  });
  const [vehicleType, setVehicleType] = useState('');
  const [plateNumber, setPlateNumber] = useState('');
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleColor, setVehicleColor] = useState('');
  const [notes, setNotes] = useState('');
  const [mapLinkError, setMapLinkError] = useState<{pickup?: string; dropoff?: string}>({});

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setIsLoadingCustomers(true);
    try {
      const data = await api.getCustomers({ limit: 100 });
      setCustomers(data.customers || []);
    } catch (err: any) {
      console.error('Failed to load customers:', err);
      setError('Failed to load customers. Please refresh the page.');
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  const handleCustomerCreated = (customerId: string) => {
    // Refresh customer list
    fetchCustomers();
    // Auto-select the newly created customer
    setSelectedCustomerId(customerId);
    setSuccess('Customer created successfully! They have been selected for this job.');
  };

  const handlePickupMapLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const link = e.target.value;
    setPickupLocation({ ...pickupLocation, mapLink: link });

    if (link) {
      const coords = parseGoogleMapsLink(link);
      if (coords) {
        setPickupLocation({
          ...pickupLocation,
          mapLink: link,
          latitude: coords.lat,
          longitude: coords.lng,
        });
        setMapLinkError({ ...mapLinkError, pickup: undefined });
        setSuccess('Coordinates extracted from Google Maps link successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setMapLinkError({ ...mapLinkError, pickup: 'Could not extract coordinates from this link. Please check the link format.' });
      }
    } else {
      setMapLinkError({ ...mapLinkError, pickup: undefined });
    }
  };

  const handleDropoffMapLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const link = e.target.value;
    setDropoffLocation({ ...dropoffLocation, mapLink: link });

    if (link) {
      const coords = parseGoogleMapsLink(link);
      if (coords) {
        setDropoffLocation({
          ...dropoffLocation,
          mapLink: link,
          latitude: coords.lat,
          longitude: coords.lng,
        });
        setMapLinkError({ ...mapLinkError, dropoff: undefined });
        setSuccess('Coordinates extracted from Google Maps link successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setMapLinkError({ ...mapLinkError, dropoff: 'Could not extract coordinates from this link. Please check the link format.' });
      }
    } else {
      setMapLinkError({ ...mapLinkError, dropoff: undefined });
    }
  };

  const validateForm = (): boolean => {
    if (!selectedCustomerId) {
      setError('Please select a customer');
      return false;
    }

    if (!serviceType) {
      setError('Please select a service type');
      return false;
    }

    // Validate pickup location address
    if (!pickupLocation.address) {
      setError('Please provide pickup address');
      return false;
    }

    // Check if either coordinates OR map link is provided
    const hasPickupCoordinates = pickupLocation.latitude && pickupLocation.longitude;
    const hasPickupMapLink = pickupLocation.mapLink && pickupLocation.mapLink.trim().length > 0;

    if (!hasPickupCoordinates && !hasPickupMapLink) {
      setError('Please provide pickup location coordinates (latitude/longitude) OR a Google Maps link');
      return false;
    }

    // If coordinates are provided, validate them
    if (hasPickupCoordinates) {
      const pickupLat = parseFloat(pickupLocation.latitude);
      const pickupLng = parseFloat(pickupLocation.longitude);

      if (isNaN(pickupLat) || isNaN(pickupLng)) {
        setError('Invalid pickup coordinates. Please enter valid numbers for latitude and longitude');
        return false;
      }

      if (pickupLat < -90 || pickupLat > 90) {
        setError('Pickup latitude must be between -90 and 90');
        return false;
      }

      if (pickupLng < -180 || pickupLng > 180) {
        setError('Pickup longitude must be between -180 and 180');
        return false;
      }
    }

    // Validate dropoff location only for towing service
    if (serviceType === 'towing') {
      if (!dropoffLocation.address) {
        setError('Please provide dropoff address for towing service');
        return false;
      }

      // Check if either coordinates OR map link is provided for dropoff
      const hasDropoffCoordinates = dropoffLocation.latitude && dropoffLocation.longitude;
      const hasDropoffMapLink = dropoffLocation.mapLink && dropoffLocation.mapLink.trim().length > 0;

      if (!hasDropoffCoordinates && !hasDropoffMapLink) {
        setError('Please provide dropoff location coordinates (latitude/longitude) OR a Google Maps link');
        return false;
      }

      // If coordinates are provided, validate them
      if (hasDropoffCoordinates) {
        const dropoffLat = parseFloat(dropoffLocation.latitude);
        const dropoffLng = parseFloat(dropoffLocation.longitude);

        if (isNaN(dropoffLat) || isNaN(dropoffLng)) {
          setError('Invalid dropoff coordinates. Please enter valid numbers for latitude and longitude');
          return false;
        }

        if (dropoffLat < -90 || dropoffLat > 90) {
          setError('Dropoff latitude must be between -90 and 90');
          return false;
        }

        if (dropoffLng < -180 || dropoffLng > 180) {
          setError('Dropoff longitude must be between -180 and 180');
          return false;
        }
      }
    }

    if (!vehicleType) {
      setError('Please select a vehicle type');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const jobData: any = {
        customerId: selectedCustomerId,
        serviceType: serviceType,
        pickupLocation: {
          address: pickupLocation.address,
          coordinates: [
            parseFloat(pickupLocation.longitude),
            parseFloat(pickupLocation.latitude),
          ] as [number, number],
        },
        vehicleDetails: {
          type: vehicleType,
          plateNumber: plateNumber || undefined,
          make: vehicleMake || undefined,
          model: vehicleModel || undefined,
          color: vehicleColor || undefined,
        },
        notes: notes || undefined,
      };

      // Only include dropoff location for towing service
      if (serviceType === 'towing') {
        jobData.dropoffLocation = {
          address: dropoffLocation.address,
          coordinates: [
            parseFloat(dropoffLocation.longitude),
            parseFloat(dropoffLocation.latitude),
          ] as [number, number],
        };
      }

      console.log('🚀 Sending job data:', JSON.stringify(jobData, null, 2));
      console.log('📝 ServiceType value:', serviceType);

      await api.createJob(jobData);

      setSuccess('Job created successfully! Drivers are being notified.');

      // Reset form
      setSelectedCustomerId('');
      setServiceType('');
      setPickupLocation({ address: '', latitude: '', longitude: '', mapLink: '' });
      setDropoffLocation({ address: '', latitude: '', longitude: '', mapLink: '' });
      setVehicleType('');
      setPlateNumber('');
      setVehicleMake('');
      setVehicleModel('');
      setVehicleColor('');
      setNotes('');
      setMapLinkError({});
    } catch (err: any) {
      console.error('Failed to create job:', err);
      setError(err.response?.data?.error || 'Failed to create job. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedCustomerId('');
    setServiceType('');
    setPickupLocation({ address: '', latitude: '', longitude: '', mapLink: '' });
    setDropoffLocation({ address: '', latitude: '', longitude: '', mapLink: '' });
    setVehicleType('');
    setPlateNumber('');
    setVehicleMake('');
    setVehicleModel('');
    setVehicleColor('');
    setNotes('');
    setError('');
    setSuccess('');
    setMapLinkError({});
  };

  return (
    <div className="create-job-page">
      <div className="page-header">
        <h1>Create New Job</h1>
        <p>Create a service job request for a customer</p>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="create-job-form">
        {/* Customer Selection */}
        <div className="form-section">
          <div className="section-header">
            <h2>Customer Information</h2>
            <button
              type="button"
              onClick={() => setShowCreateCustomerModal(true)}
              className="btn-create-customer"
            >
              ➕ New Customer
            </button>
          </div>
          <div className="form-group">
            <label htmlFor="customer">
              Select Customer <span className="required">*</span>
            </label>
            {isLoadingCustomers ? (
              <div className="loading-text">Loading customers...</div>
            ) : (
              <select
                id="customer"
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                required
              >
                <option value="">-- Select a customer --</option>
                {customers.map((customer) => (
                  <option key={customer._id} value={customer._id}>
                    {customer.firstName} {customer.lastName} - {customer.phone}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Service Type */}
        <div className="form-section">
          <h2>Service Type</h2>
          <div className="form-group">
            <label htmlFor="service-type">
              Select Service <span className="required">*</span>
            </label>
            <select
              id="service-type"
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value)}
              required
            >
              <option value="">-- Select a service --</option>
              <option value="towing">🚚 Towing Service</option>
              <option value="petrol_delivery">⛽ Petrol Delivery</option>
              <option value="jumpstart">🔋 Jump Start</option>
              <option value="tire_change">🛞 Tire Change</option>
              <option value="lockout_service">🔑 Lockout Service</option>
              <option value="winch_out">⛓️ Winch Out</option>
            </select>
          </div>
        </div>

        {/* Pickup Location */}
        <div className="form-section">
          <h2>Pickup Location</h2>
          <div className="form-group">
            <label htmlFor="pickup-address">
              Address <span className="required">*</span>
            </label>
            <input
              id="pickup-address"
              type="text"
              value={pickupLocation.address}
              onChange={(e) => setPickupLocation({ ...pickupLocation, address: e.target.value })}
              placeholder="Enter pickup address"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="pickup-map-link">
              Google Maps Link
            </label>
            <input
              id="pickup-map-link"
              type="text"
              value={pickupLocation.mapLink || ''}
              onChange={handlePickupMapLinkChange}
              placeholder="Paste Google Maps link here (e.g., https://maps.google.com/...)"
              className={mapLinkError.pickup ? 'input-error' : ''}
            />
            {mapLinkError.pickup && (
              <div className="error-text">{mapLinkError.pickup}</div>
            )}
            <small className="help-text">
              Paste a Google Maps link to automatically fill coordinates, OR enter latitude/longitude manually below
            </small>
          </div>

          <div className="location-divider">
            <span>OR</span>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="pickup-latitude">
                Latitude
              </label>
              <input
                id="pickup-latitude"
                type="text"
                value={pickupLocation.latitude}
                onChange={(e) => setPickupLocation({ ...pickupLocation, latitude: e.target.value })}
                placeholder="e.g., 10.8505"
              />
            </div>
            <div className="form-group">
              <label htmlFor="pickup-longitude">
                Longitude
              </label>
              <input
                id="pickup-longitude"
                type="text"
                value={pickupLocation.longitude}
                onChange={(e) => setPickupLocation({ ...pickupLocation, longitude: e.target.value })}
                placeholder="e.g., 76.2711"
              />
            </div>
          </div>
        </div>

        {/* Dropoff Location - Only for Towing */}
        {serviceType === 'towing' && (
        <div className="form-section">
          <h2>Dropoff Location</h2>
          <div className="form-group">
            <label htmlFor="dropoff-address">
              Address <span className="required">*</span>
            </label>
            <input
              id="dropoff-address"
              type="text"
              value={dropoffLocation.address}
              onChange={(e) => setDropoffLocation({ ...dropoffLocation, address: e.target.value })}
              placeholder="Enter dropoff address"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="dropoff-map-link">
              Google Maps Link
            </label>
            <input
              id="dropoff-map-link"
              type="text"
              value={dropoffLocation.mapLink || ''}
              onChange={handleDropoffMapLinkChange}
              placeholder="Paste Google Maps link here (e.g., https://maps.google.com/...)"
              className={mapLinkError.dropoff ? 'input-error' : ''}
            />
            {mapLinkError.dropoff && (
              <div className="error-text">{mapLinkError.dropoff}</div>
            )}
            <small className="help-text">
              Paste a Google Maps link to automatically fill coordinates, OR enter latitude/longitude manually below
            </small>
          </div>

          <div className="location-divider">
            <span>OR</span>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dropoff-latitude">
                Latitude
              </label>
              <input
                id="dropoff-latitude"
                type="text"
                value={dropoffLocation.latitude}
                onChange={(e) => setDropoffLocation({ ...dropoffLocation, latitude: e.target.value })}
                placeholder="e.g., 10.8505"
              />
            </div>
            <div className="form-group">
              <label htmlFor="dropoff-longitude">
                Longitude
              </label>
              <input
                id="dropoff-longitude"
                type="text"
                value={dropoffLocation.longitude}
                onChange={(e) => setDropoffLocation({ ...dropoffLocation, longitude: e.target.value })}
                placeholder="e.g., 76.2711"
              />
            </div>
          </div>
        </div>
        )}

        {/* Vehicle Details */}
        <div className="form-section">
          <h2>Vehicle Details</h2>
          <div className="form-group">
            <label htmlFor="vehicle-type">
              Vehicle Type <span className="required">*</span>
            </label>
            <select
              id="vehicle-type"
              value={vehicleType}
              onChange={(e) => setVehicleType(e.target.value)}
              required
            >
              <option value="">-- Select vehicle type --</option>
              <option value="car">Car</option>
              <option value="suv">SUV</option>
              <option value="truck">Truck</option>
              <option value="motorcycle">Motorcycle</option>
              <option value="van">Van</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="plate-number">Plate Number</label>
              <input
                id="plate-number"
                type="text"
                value={plateNumber}
                onChange={(e) => setPlateNumber(e.target.value)}
                placeholder="e.g., KL-01-AB-1234"
              />
            </div>
            <div className="form-group">
              <label htmlFor="vehicle-make">Make</label>
              <input
                id="vehicle-make"
                type="text"
                value={vehicleMake}
                onChange={(e) => setVehicleMake(e.target.value)}
                placeholder="e.g., Toyota"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="vehicle-model">Model</label>
              <input
                id="vehicle-model"
                type="text"
                value={vehicleModel}
                onChange={(e) => setVehicleModel(e.target.value)}
                placeholder="e.g., Camry"
              />
            </div>
            <div className="form-group">
              <label htmlFor="vehicle-color">Color</label>
              <input
                id="vehicle-color"
                type="text"
                value={vehicleColor}
                onChange={(e) => setVehicleColor(e.target.value)}
                placeholder="e.g., White"
              />
            </div>
          </div>
        </div>

        {/* Additional Notes */}
        <div className="form-section">
          <h2>Additional Information</h2>
          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any special instructions or notes about the job..."
              rows={4}
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handleReset}
            className="btn-secondary"
            disabled={isSubmitting}
          >
            Reset Form
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating Job...' : 'Create Job'}
          </button>
        </div>
      </form>

      {/* Create Customer Modal */}
      <CreateCustomerModal
        isOpen={showCreateCustomerModal}
        onClose={() => setShowCreateCustomerModal(false)}
        onCustomerCreated={handleCustomerCreated}
      />
    </div>
  );
};

export default CreateJob;
